import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../components/BreadCrumb";
import { MdOutlineMyLocation } from "react-icons/md";

import { useEffect, useState, useRef } from "react";
import SummaryApi from "../common/SummaryApi";
import apiRequest from "../common/apiRequest"; 
import { toast } from "react-hot-toast";
import DeleteConfirmModal from "../helpers/DeleteConfirmModal";
import { MoreVertical } from "lucide-react";

export const AddressActionMenu = ({
  onEdit,
  onDelete,
  onSetDefault,
  isDefault,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      {/* 3 DOT BUTTON */}
      <button
        onClick={() => setOpen(prev => !prev)}
        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
      >
        <MoreVertical size={18} />
      </button>

      {/* MENU */}
      {open && (
        <div className="absolute right-0 mt-2 w-40 bg-white border rounded-lg shadow-lg z-20">
          <button
            onClick={() => {
              onEdit();
              setOpen(false);
            }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
          >
            Edit
          </button>

          {!isDefault && (
            <button
              onClick={() => {
                onSetDefault();
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
            >
              Set as Default
            </button>
          )}

          <button
            onClick={() => {
              onDelete();
              setOpen(false);
            }}
            className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

const AddressSkeleton = () => {
  return (
    <div className="bg-white rounded-xl px-4 py-6 animate-pulse">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="flex items-center justify-between py-5 border-b last:border-b-0"
        >
          {/* LEFT */}
          <div className="flex items-center gap-3 w-full">
            {/* Location icon skeleton */}
            <div className="w-8 h-8 rounded-full bg-gray-200 shrink-0" />

            {/* Text skeleton */}
            <div className="space-y-2 w-full">
              <div className="h-3 w-1/3 bg-gray-200 rounded" />
              <div className="h-3 w-2/3 bg-gray-200 rounded" />
              <div className="h-3 w-1/4 bg-gray-200 rounded" />
            </div>
          </div>

          {/* RIGHT → 3-DOT MENU */}
          <div className="w-8 h-8 rounded-full bg-gray-200 shrink-0" />
        </div>
      ))}
    </div>
  );
};

const EmptyAddress = ({ onAdd }) => {
  return (
    <div className="rounded-xl px-6 py-12 text-center">
      <div className="flex justify-center mb-4">
        <svg
        width="96"
        height="96"
        viewBox="0 0 96 96"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Circle background */}
        <circle cx="48" cy="48" r="48" fill="#EAF2FF" />

        {/* Location pin */}
        <path
          d="M48 24C38.6 24 31 31.6 31 41c0 12.5 17 31 17 31s17-18.5 17-31c0-9.4-7.6-17-17-17z"
          fill="#2563EB"
        />

        {/* Inner dot */}
        <circle cx="48" cy="41" r="5" fill="white" />

        {/* Card */}
        <rect
          x="30"
          y="62"
          width="36"
          height="16"
          rx="4"
          fill="white"
          stroke="#CBD5E1"
        />

        {/* Lines */}
        <rect x="34" y="66" width="20" height="2" rx="1" fill="#CBD5E1" />
        <rect x="34" y="70" width="14" height="2" rx="1" fill="#CBD5E1" />
      </svg>
      </div>

      <h3 className="text-sm font-semibold mb-1">
        No addresses found
      </h3>

      <p className="text-xs text-gray-500 mb-6">
        You haven't added any delivery address yet.
      </p>

      <button
        onClick={onAdd}
        className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-[#007BFF] text-white text-sm font-medium"
      >
        Add New Address
      </button>
    </div>
  );
};

const AddressPage = () => {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [search, setSearch] = useState("");

  const loadAddresses = async () => {
    try {
      setLoading(true);
      const res = await apiRequest(SummaryApi.getMyAddresses);
      setAddresses(res.addresses);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAddresses();
  }, []);


  const handleDelete = async () => {
    try {
      setDeleteLoading(true);

      await apiRequest({
        ...SummaryApi.deleteAddress,
        url: SummaryApi.deleteAddress.url(deleteId),
      });

      toast.success("Address deleted");

      setAddresses(prev =>
        prev.filter(a => a._id !== deleteId)
      );
      setDeleteId(null);
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to delete address"
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEnableLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;

          await apiRequest(
            SummaryApi.addAddressByLocation,
            {
              lat: latitude,
              lng: longitude,
            }
          );

          toast.success("Location added successfully");

          // 🔁 REFETCH ADDRESS LIST
          loadAddresses();

        } catch (err) {
          toast.error(
            err.response?.data?.message || "Failed to save location"
          );
        }
      },
      () => toast.error("Location permission denied")
    );
  };

  const handleSetDefault = async (id) => {
  try {
    await apiRequest({
      ...SummaryApi.setDefaultAddress,
      url: SummaryApi.setDefaultAddress.url(id),
    });

    toast.success("Default address updated");

    // 🔁 update locally
    setAddresses(prev =>
      prev.map(a => ({
        ...a,
        isDefault: a._id === id,
      }))
    );
  } catch (err) {
    toast.error("Failed to set default address");
  }
};

const filteredAddresses = addresses.filter((addr) => {
  const keyword = search.toLowerCase();

  return (
    addr.name?.toLowerCase().includes(keyword) ||
    addr.city?.toLowerCase().includes(keyword) ||
    addr.state?.toLowerCase().includes(keyword) ||
    addr.area?.toLowerCase().includes(keyword) ||
    addr.pincode?.includes(keyword)
  );
});


  return (
    <div className="">
      <div className=" mx-auto space-y-6">

        {/* TITLE */}
        {/* HEADER */}
        <div>
          <h1 className="hidden lg:block text-xl font-bold mb-2">Address</h1>

          <Breadcrumb
            items={[
              { label: "Home", path: "/" },
              { label: "My Account", path: "/my-account/menu" },
              { label: "Address" }
            ]}
          />
        </div>


        {/* SEARCH */}
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search address, city, name"
            className="
              w-full pl-9 py-2.5 rounded-lg
              border border-[#DDE4FF]
              bg-[#F5F8FF]
              outline-none
            "
          />
        </div>

        {/* LOCATION + ADD NEW (SINGLE CARD) */}
        <div className="bg-white rounded-xl px-4 py-6 space-y-4">

          <div className="flex items-center justify-between">
            <div className="flex gap-3 items-start">
              <MdOutlineMyLocation className="text-[#007BFF] mt-1" size={24} />
              <div>
                <p className="text-sm font-medium text-[#007BFF]">
                  Detect current location
                </p>
                <p className="text-xs text-[#8080808C]/55 font-medium">
                  Enable your current location
                </p>
              </div>
            </div>

          <button
            onClick={handleEnableLocation}
            className="border border-[#007BFF] text-[#007BFF] px-4 py-1.5 rounded-lg text-sm font-medium"
          >
            Enable
          </button>

          </div>
        <div className="border-t border-dashed" />
        <button
          onClick={() => navigate("/my-account/address/new")}
          className="w-full text-center text-[#007BFF] text-sm font-medium pt-4"
        >
          Add New Address
        </button>
        </div>

      {/* SAVED ADDRESS */}
      <div>
        <h3 className="text-sm font-medium mb-4">Saved Address</h3>

        {loading ? (
          <AddressSkeleton />
        ) : filteredAddresses.length === 0 ? (
          <EmptyAddress
            onAdd={() => navigate("/my-account/address/new")}
          />
        ) : (
          <div className="divide-y divide-dashed bg-white rounded-xl px-4 py-6">
            {filteredAddresses.map((addr) => (
              <div
                key={addr._id}
                className="flex items-center justify-between pt-4 pb-6"
              >
                {/* LEFT */}
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <MdOutlineMyLocation size={24} className="text-[#191C1F]" />
                  </div>

                  <div className="text-sm leading-5">
                    <p className="font-medium flex items-center gap-2">
                      {addr.name}

                      {addr.isDefault && (
                        <span className="text-xs bg-[#007BFF] text-white px-2 py-0.5 rounded-sm">
                          Default
                        </span>
                      )}
                    </p>
                    <p>
                      {addr.flat}, {addr.area}, {addr.city},{" "}
                      {addr.state} {addr.pincode}
                    </p>
                      {addr.phone && (
                        <p className="text-xs mt-1">
                          Phone number: {addr.phone}
                        </p>
                      )}                    
                  </div>
                </div>

                {/* ACTIONS */}
                <div className="flex gap-2">
                <AddressActionMenu
                  isDefault={addr.isDefault}
                  onEdit={() =>
                    navigate(`/my-account/address/edit/${addr._id}`)
                  }
                  onDelete={() => setDeleteId(addr._id)}
                  onSetDefault={() => handleSetDefault(addr._id)}
                />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
      <DeleteConfirmModal
        open={!!deleteId}
        title="Delete Address"
        description="This address will be permanently removed."
        loading={deleteLoading}
        onCancel={() => setDeleteId(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default AddressPage;
